import React, { useEffect, useMemo, useState } from "react";
import { animated, useTransition } from "@react-spring/web";

import Actions from "src/components/actionbar";
import Button from "src/components/button";
import Reveal from "src/components/animation";
import EmptyMessage from "src/components/emptyMessage";
import { toast } from "react-toastify";
import Toast from "src/components/toast";
import MenuPane from "./menuPane";
import DetailView from "./detailView";

import {
  useCreateCustomFieldMutation,
  useCustomFieldsQuery,
  useUpdateCustomField,
} from "src/query/settings/customFields";

import { useCustomFieldTracking } from "src/query/settings/adoption/customFields";

import { CustomField } from "src/types/settings/customFields";

import _ from "lodash";
import styles from "./styles.module.scss";


const CustomFields: React.FC = () => {

  const [selectedCustomField, setSelectedCustomField] = useState<CustomField | null>(null);
  const [isCreatingNewField, setIsCreatingNewField] = useState(false);
  const [isFieldModified, setIsFieldModified] = useState(false);

  const { data: items = [], isLoading: isCustomFieldsLoading, refetch} = useCustomFieldsQuery();

  const createCustomFieldMutation = useCreateCustomFieldMutation();

  const updateCustomFieldMutation = useUpdateCustomField();

  const { trackCustomFieldClicked, trackCustomFieldCreated } = useCustomFieldTracking();

  const hideAddButton = isCreatingNewField || isFieldModified;

  const isMutationPending =
    createCustomFieldMutation.isPending || updateCustomFieldMutation.isPending;

  const handleSetSelectedCustomField = (item: CustomField) => {
    setSelectedCustomField(item);
  };

  const handleCustomFieldModified = (item: CustomField) => {
    setSelectedCustomField(item);
    setIsFieldModified(true);
  };

  const handleClearSelectedItem = () => {
    setSelectedCustomField(null);
  };

  const canDelete = !!selectedCustomField?.id;

  // This is used to render the new created Custom Field at the top until saved.
  const derivedItems = useMemo(() => {
    if (!selectedCustomField || selectedCustomField.id !== "") return items;
    return [selectedCustomField, ...items];
  }, [items, selectedCustomField]);

  // This function handles the success of creating or updating a custom field.
  const handleCustomFieldSuccess = () => {
    if (!selectedCustomField) return;

    trackCustomFieldCreated(selectedCustomField);

    setIsFieldModified(false);
    setIsCreatingNewField(false);
  };

  // This function validates the custom field before saving it.
  const validateCustomField = (field: CustomField | null): boolean => {
    if (!field) return false;

    const name = field.name?.trim();
    if (!name || name.length === 0) {
      toast(
        <Toast.warning
          heading="Warning"
          message="Enter a valid name."
        />
      );
      return false;
    }

    if (!field.options || _.size(field.options) === 0) {
      toast(
        <Toast.warning
          heading="Warning"
          message="List values cannot be empty."
        />
      );
      return false;
    }

    const hasEmptyOption = field.options.some(
      (opt) => !opt.value || opt.value.trim().length === 0
    );
  
    if (hasEmptyOption) {
      toast(
        <Toast.warning
          heading="Warning"
          message="List values cannot be empty."
        />
      );
      return false;
    }
  
    return true;
  };


  // Handle saving changes for both adding and updating custom fields
  const handleSave = () => {
    if (!selectedCustomField) return;

    if (!validateCustomField(selectedCustomField)) return;

    if (isCreatingNewField) {
      createCustomFieldMutation.mutate(
        {
          name: selectedCustomField.name,
          helpText: selectedCustomField.helpText,
          multiselect: selectedCustomField.multiselect,
          searchable: selectedCustomField.searchable,
          options: selectedCustomField.options,
          type: selectedCustomField.type,
        },
        {
          onSuccess: handleCustomFieldSuccess,
          onError: (error) => {
            console.error("Error creating custom field", error);
            toast(
              <Toast.Failure
                heading="Failed to create field"
                message="Something went wrong. Please try again."
              />
            );
          }
        },
      );
    } else {
      updateCustomFieldMutation.mutate(
        {
          id: selectedCustomField.id,
          data: {
            name: selectedCustomField.name,
            helpText: selectedCustomField.helpText,
            multiselect: selectedCustomField.multiselect,
            searchable: selectedCustomField.searchable,
            options: selectedCustomField.options || [],
          },
        },
        {
          onSuccess: handleCustomFieldSuccess,
          onError: (error) => {
            console.error("Error updating custom field", error);
            toast(
              <Toast.Failure
                heading="Failed to create field"
                message="Something went wrong. Please try again."
              />
            );
          },
        },
      );
    }
  };

  // Handle cancel action to reset changes or fallback to the first custom field in case of adding a new one.
  const handleCancel = () => {
    setIsFieldModified(false);

    if (isCreatingNewField) {
      setIsCreatingNewField(false);
      setSelectedCustomField(items[0] || null);
      return;
    }

    const originalItem = items.find(item => item.id === selectedCustomField?.id);
    if (originalItem) {
      setSelectedCustomField({ ...originalItem });
    }
  };

  //This function handles the click on the "Add Custom Field" button.
  const handleAddClick = () => {
    trackCustomFieldClicked();

    setIsCreatingNewField(true);
    const newField: CustomField = {
      id: "",
      type: "list_field",
      name: "Untitled",
      helpText: "",
      multiselect: false,
      searchable: true,
      options: [],
      modifiedDate: Date.now(),
    };
    setSelectedCustomField(newField);
  }

  // This transition handles the visibility and animation of the "Add Custom Field" button.
  const addButtonTransitions = useTransition(!hideAddButton, {
    initial: { opacity: 0, transform: "translateY(0px)" },
    from: { opacity: 0, transform: "translateY(-10px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-10px)" },
  });

  useEffect(() => {
    if (!selectedCustomField && _.size(items) > 0 && !isCreatingNewField) {
      setSelectedCustomField(items[0]);
    }
  }, [items, selectedCustomField, isCreatingNewField]);

  useEffect(() => {
    if (_.size(items) === 0) {
      setSelectedCustomField(null);
    } else if (!selectedCustomField || !items.find((i) => i.id === selectedCustomField.id)) {
      setSelectedCustomField(items[0]);
    }
  }, [items]);

  useEffect(() => {
    if (!selectedCustomField || !items) return;
  
    const updated = items.find(item => item.id === selectedCustomField.id);
    if (updated) {
      setSelectedCustomField(updated);
    }
  }, [items, selectedCustomField?.id]);

  const renderEmptyMessage = () => {
    return (
      <div className={styles.emptyMessage}>
        <EmptyMessage
          image={"/images/settings/custom_fields/no_custom_fields.svg"}
          heading="No custom fields yet!"
          action={{
            title: "add custom field",
            icon: "paperflite-add",
            onClick: handleAddClick,
          }}
        />
      </div>
    );
  }

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.heading}> Custom Fields</div>
          <div className={styles.subHeading}>
            Create fields to tag, filter, and personalize your content.
          </div>
        </div>
        <div className={styles.actionButtons}>
          {_.size(derivedItems) > 0 &&
            addButtonTransitions((style, isVisible) =>
              isVisible && (
                <animated.div style={{ position: "absolute", right: 0, ...style }}>
                  <Actions.Button
                    width={3}
                    icon="paperflite-add"
                    title="add custom field"
                    onClick={handleAddClick}
                  />
                </animated.div>
              )
            )}
          <Reveal show={isCreatingNewField || isFieldModified}>
            <div className={styles.actions}>

              <div className={styles.actionsInfo}>click here to</div>
              <Button
                variant="primary"
                disabled={isMutationPending}
                text={isMutationPending ? "Saving..." : "Save"}
                icon="paperflite-tick"
                onClick={handleSave}
              />
              <Button variant="secondary" text="Cancel" onClick={handleCancel} />
            </div>
          </Reveal>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.menuPane}>
          {!isCustomFieldsLoading && _.size(derivedItems) === 0 && <CustomFieldPlaceholder />}

          {_.size(derivedItems) > 0 && (
            <MenuPane
              items={derivedItems}
              selectedCustomField={selectedCustomField}
              onSelectCustomField={handleSetSelectedCustomField}
              isCreatingNewField={isCreatingNewField}
            />
          )}
        </div>

        <div className={styles.detailView}>
          {selectedCustomField && (
            <DetailView
              selectedCustomField={selectedCustomField}
              onSelectCustomField={handleCustomFieldModified}
              clearSelectedItem={handleClearSelectedItem}
              renderEmptyMessages={renderEmptyMessage}
              canDelete={canDelete}
            />
          )}

          {!selectedCustomField && !isCustomFieldsLoading && _.size(items) === 0 && (
            <div className={styles.container}>
              {renderEmptyMessage()}
            </div>
          )}
        </div>
      </div>
    </div>

  );
};

export default CustomFields;

const CustomFieldPlaceholder = () => {
  return (
    <div className={styles.placeholderWrapper}>
      <div className={styles.placeholderGroup}>
        <div className={styles.primaryRowStrong} />
        <div className={styles.secondaryRowStrong} />
      </div>
      <div className={styles.placeholderGroup}>
        <div className={styles.primaryRowMedium} />
        <div className={styles.secondaryRowMedium} />
      </div>
      <div className={styles.placeholderGroup}>
        <div className={styles.primaryRowFaint} />
        <div className={styles.secondaryRowFaint} />
      </div>
    </div>
  );
};
